import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Plus, Minus, Box, ExternalLink, ChevronRight, Loader2, RefreshCw, Check } from "lucide-react";
import client, { displayName, priceLabel, hasPrice } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { TechnicianBar } from "@/components/TechnicianBar";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

const Spec = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b border-border last:border-0">
    <span className="text-sm text-steel-light">{label}</span>
    <span className="mono text-sm text-graphite text-right">{value || "—"}</span>
  </div>
);

const ProductDetail = () => {
  const { sku } = useParams();
  const navigate = useNavigate();
  const { addToCart, favourites, toggleFav, addRecent } = useStore();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setLoading(true); setImg(0); setQty(1);
    client.get(`/products/${encodeURIComponent(sku)}`)
      .then((r) => { setP(r.data); addRecent(r.data.sku); })
      .catch(() => setP(null))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [sku]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-steel-light" /></div>;
  if (!p) return (
    <div className="min-h-screen flex flex-col"><Navbar /><div className="flex-1 grid place-items-center"><div className="text-center"><p className="text-steel">Product not found.</p><Link to="/catalogue" className="text-orange">Back to catalogue</Link></div></div></div>
  );

  const fav = favourites.includes(p.sku);
  const images = p.images?.length ? p.images : [];
  const fmtDate = (d) => d ? new Date(d).toLocaleString() : "—";

  return (
    <div className="App min-h-screen flex flex-col bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full py-6 flex-1">
        {/* breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm text-steel-light mb-5 flex-wrap">
          <Link to="/" className="hover:text-graphite">Home</Link><ChevronRight className="w-3.5 h-3.5" />
          {p.category_slug && <><Link to={`/category/${p.category_slug}`} className="hover:text-graphite">{p.category}</Link><ChevronRight className="w-3.5 h-3.5" /></>}
          <span className="mono text-graphite">{p.sku}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* gallery */}
          <div>
            <div className="gp-card-img rounded-lg border border-border aspect-square grid place-items-center p-8">
              {images[img] ? <img src={images[img]} alt={displayName(p)} className="max-h-full max-w-full object-contain mix-blend-multiply" /> : <Box className="w-16 h-16 text-steel-light/40" />}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto thin-scroll">
                {images.map((src, i) => (
                  <button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setImg(i)} className={`w-16 h-16 shrink-0 rounded-md border grid place-items-center p-1.5 gp-card-img ${img === i ? "border-orange" : "border-border"}`}>
                    <img src={src} alt="" className="max-h-full max-w-full object-contain mix-blend-multiply" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* summary */}
          <div>
            {p.brand && <Link to={`/brand/${encodeURIComponent(p.brand)}`} className="inline-block mono text-xs uppercase tracking-[0.15em] text-orange mb-2">{p.brand}</Link>}
            <h1 data-testid="product-title" className="text-2xl sm:text-3xl font-bold tracking-tight text-graphite leading-tight">{displayName(p)}</h1>
            <div className="flex items-center gap-3 mt-3">
              <span className="mono text-sm bg-surface border border-border rounded px-2 py-1 text-steel">SKU: {p.sku}</span>
              <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${p.is_in_stock ? "text-emerald-600" : "text-steel-light"}`}>
                <span className={`w-2 h-2 rounded-full ${p.is_in_stock ? "bg-emerald-500" : "bg-steel-light"}`} />{p.stock_status}
              </span>
            </div>

            <div className="mt-5 p-4 rounded-lg bg-surface border border-border">
              <div className="text-xs uppercase tracking-wider text-steel-light">Price</div>
              <div className="text-2xl font-bold text-graphite mt-1">{priceLabel(p)}</div>
              {!hasPrice(p) && <div className="text-xs text-steel-light mt-1">Public price not listed — add to cart and request a quote.</div>}
            </div>

            <div className="mt-5 flex items-center gap-3">
              <div className="flex items-center border border-border rounded-md h-12">
                <button data-testid="detail-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-full grid place-items-center text-steel hover:text-graphite"><Minus className="w-4 h-4" /></button>
                <span className="mono w-12 text-center text-lg">{qty}</span>
                <button data-testid="detail-qty-plus" onClick={() => setQty((q) => q + 1)} className="w-11 h-full grid place-items-center text-steel hover:text-graphite"><Plus className="w-4 h-4" /></button>
              </div>
              <button data-testid="detail-add-to-cart" onClick={() => { addToCart(p.sku, qty); toast.success(`Added ${qty} × ${p.sku}`); }} className="flex-1 h-12 inline-flex items-center justify-center gap-2 bg-orange hover:bg-orange-hover text-white font-semibold rounded-md transition-colors">
                <Plus className="w-5 h-5" /> Add to Cart
              </button>
              <button data-testid="detail-fav" onClick={() => toggleFav(p.sku)} className={`w-12 h-12 grid place-items-center rounded-md border transition-colors ${fav ? "bg-orange/10 border-orange text-orange" : "border-border text-steel hover:text-graphite"}`}>
                <Heart className="w-5 h-5" fill={fav ? "currentColor" : "none"} />
              </button>
            </div>

            {p.short_description && <p className="mt-6 text-steel leading-relaxed">{p.description || p.short_description}</p>}

            {/* specs */}
            <div className="mt-6 border border-border rounded-lg p-4">
              <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-2">Specifications</div>
              <Spec label="SKU" value={p.sku} />
              <Spec label="Brand" value={p.brand} />
              <Spec label="Category" value={p.categories?.join(" / ")} />
              <Spec label="Weight" value={p.weight} />
              <Spec label="Dimensions" value={p.dimensions} />
              {p.attributes?.map((a) => <Spec key={a.name} label={a.name} value={(a.values || []).join(", ")} />)}
              <Spec label="Availability" value={p.stock_status} />
            </div>

            <div className="mt-4 flex flex-col gap-1.5 text-xs text-steel-light">
              <div className="flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Last synchronised: <span className="mono">{fmtDate(p.last_synced)}</span></div>
              {p.original_url && <a href={p.original_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-steel hover:text-orange"><ExternalLink className="w-3.5 h-3.5" /> View original product page</a>}
            </div>
          </div>
        </div>

        {/* related */}
        {p.related?.length > 0 && (
          <section className="mt-14">
            <h2 className="text-xl font-bold tracking-tight text-graphite mb-5">Related Products</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {p.related.slice(0, 4).map((rp) => <ProductCard key={rp.sku} product={rp} />)}
            </div>
          </section>
        )}
      </div>
      <Footer />
      <TechnicianBar />
    </div>
  );
};

export default ProductDetail;
